import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CooperationGroupsListComponent } from '@app/components/cooperations/cooperation-groups-list/cooperation-groups-list.component';
import { CooperationProjectsListComponent } from '@app/components/cooperations/cooperation-projects-list/cooperation-projects-list.component';
import { CooperationUsersListComponent } from '@app/components/cooperations/cooperation-users-list/cooperation-users-list.component';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { CooperationActionsService } from '@app/shared/services/actions/cooperation-actions.service';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { CooperationsDataService } from '@app/shared/services/cooperations-data.service';
import { CooperationStatusEnum } from '@app/shared/services/dtos/cooperation/cooperation-status.enum';
import { Cooperation } from '@app/shared/types/models/cooperation/cooperation.model';
import { UiPageWrapperComponent } from '@app/shared/ui/page-wrapper/ui-page-wrapper.component';
import { TranslatePipe } from '@ngx-translate/core';
import { map } from 'rxjs/operators';

@Component({
  selector: 'proffeo-incoming-cooperation-view',
  standalone: true,
  imports: [
    UiPageWrapperComponent,
    TranslatePipe,
    RouterModule,
    UiButtonComponent,
    CooperationGroupsListComponent,
    CooperationProjectsListComponent,
    CooperationUsersListComponent
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './incoming-cooperation-view.component.html'
})
export class IncomingCooperationViewComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cooperationsDataService = inject(CooperationsDataService);
  private readonly activeCompanyService = inject(ActiveCompanyService);
  private readonly cooperationActionsService = inject(CooperationActionsService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  protected readonly CooperationStatusEnum = CooperationStatusEnum;

  protected readonly cooperationId: WritableSignal<string | undefined> = signal(undefined);
  protected readonly companyId = computed(() => this.activeCompanyService.activeCompanyId());
  protected cooperation: Signal<Cooperation | undefined> = computed(() =>
    this.cooperationId() ? this.cooperationsDataService.getById(this.cooperationId()!)() : undefined
  );
  protected partnerName: Signal<string> = computed(() => this.cooperation()?.requestorCompany()?.name ?? '');
  protected statusLabel: Signal<string> = computed(() =>
    this.cooperationsDataService.getStatusLabelKey(this.cooperation()?.status)
  );

  public constructor() {
    this.route.paramMap
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(m => m.get('cooperation-id'))
      )
      .subscribe(cooperationId => {
        if (cooperationId) {
          this.cooperationsDataService.loadById(cooperationId);
          this.cooperationId.set(cooperationId);
        }
      });
  }

  protected goBack(): void {
    const cid = this.companyId();
    if (cid) void this.router.navigate(['/companies', cid, 'cooperations']);
  }

  protected accept(): void {
    const id = this.cooperation()?.id;
    const cid = this.companyId();
    if (!id || !cid) return;
    this.cooperationActionsService.accept(id, cid).subscribe();
  }

  protected reject(): void {
    const id = this.cooperation()?.id;
    const cid = this.companyId();
    if (!id || !cid) return;
    this.cooperationActionsService.reject(id, cid).subscribe();
  }
}
